import { useMemo } from 'react';
import { z } from 'zod';
import { EmptyValidationMessage } from '@ninja/utilities';
import { Relationship, getYearOfBirth } from '../../../../../common';
import {
	useDateOfBirthField,
	useGenderField,
	useMaritalStatusField,
	useNationalityField,
	useSalaryBandField,
} from '../../../members/members-form/fields';
import { prepareFieldError } from '../../../members/members-form/helpers/add-field-error';
import { useEmiratesIdField, useIsWithEidField } from '../fields';
import { IEidMembersForm } from '../types';

export const useEidDomesticWorkerFieldsStructure = (isWithEID?: boolean) => {
	const dateOfBirthField = useDateOfBirthField<IEidMembersForm>();
	const genderField = useGenderField<IEidMembersForm>();
	const maritalStatusField = useMaritalStatusField<IEidMembersForm>();
	const nationalityField = useNationalityField<IEidMembersForm>();
	const salaryBandField = useSalaryBandField<IEidMembersForm>();
	const isWithEidField = useIsWithEidField<IEidMembersForm>();
	const emiratesIdField = useEmiratesIdField<IEidMembersForm>();
	const fieldsStructure = useMemo(
		() => [
			...(isWithEID
				? [emiratesIdField.getField({ field: 'domesticWorker.emiratesIdNumber' })]
				: [nationalityField.getField({ field: 'domesticWorker.nationalityId' })]),
			maritalStatusField.getField({ field: 'domesticWorker.maritalStatusId' }),
			genderField.getField({ field: 'domesticWorker.genderId' }),
			dateOfBirthField.getField({ field: 'domesticWorker.dateOfBirth', fieldType: 'adult' }),
			salaryBandField.getField({ field: 'domesticWorker.salaryBandId' }),
		],
		[genderField.getField, nationalityField.getField, dateOfBirthField.getField, salaryBandField.getField]
	);

	const isWithEidFieldsStructure = () => [isWithEidField.getField({ field: 'domesticWorker.isWithEID' })];

	const getValidations = ({ formValues }: { formValues: IEidMembersForm }) =>
		z.object({
			domesticWorker: z
				.object({
					genderId: z.number().optional(),
					nationalityId: z.number().optional(),
					dateOfBirth: z.string().optional(),
					salaryBandId: z.number().optional(),
					memberId: z.string().optional(),
					maritalStatusId: z.number().optional(),
					emiratesIdNumber: z.string().optional(),
					isWithEID: z.number().optional(),
				})
				.superRefine((values, ctx) => {
					const { dateOfBirth, genderId, nationalityId, salaryBandId, maritalStatusId, emiratesIdNumber, isWithEID } =
						values;
					const addFieldError = prepareFieldError(ctx);

					if (!dateOfBirth) addFieldError({ field: 'dateOfBirth', message: EmptyValidationMessage });

					if (isWithEID) {
						emiratesIdField.validate(emiratesIdNumber, addFieldError, getYearOfBirth(dateOfBirth), ctx);
					}

					if (!genderId) addFieldError({ field: 'genderId', message: EmptyValidationMessage });

					if (!isWithEID) {
						nationalityField.getValidation(nationalityId, formValues.policyTypeId, Relationship.DomesticWorker, ctx);
					}

					if (!salaryBandId) addFieldError({ field: 'salaryBandId', message: EmptyValidationMessage });

					if (!maritalStatusId) addFieldError({ field: 'maritalStatusId', message: EmptyValidationMessage });

					if (dateOfBirth)
						dateOfBirthField.getValidation({
							formValues,
							fieldType: 'adult',
							addFieldError,
							dateOfBirth,
						});
				}),
		});

	return {
		fieldsStructure,
		getValidations,
		isWithEidFieldsStructure,
	};
};
